import type {
  ChatAssistantSegment,
  ChatToolActivity,
  ChatToolActivityStatus,
  RuntimeSseEvent,
  RuntimeToolSseEvent,
} from '@/features/shared/chat/types'

const TERMINAL_TOOL_STATUSES = new Set<ChatToolActivityStatus>([
  'cancelled',
  'error',
  'success',
  'unknown',
])

/** 判断 native runtime event 是否属于稳定 Tool lifecycle union。 */
export function isRuntimeToolSseEvent(event: RuntimeSseEvent): event is RuntimeToolSseEvent {
  return (
    event.event === 'tool_start' ||
    event.event === 'tool_progress' ||
    event.event === 'tool_end' ||
    event.event === 'tool_error'
  )
}

function readSequence(event: RuntimeToolSseEvent) {
  return Number.isInteger(event.sequence) && (event.sequence ?? 0) > 0 ? event.sequence : undefined
}

function eventMatchesActivity(activity: ChatToolActivity, event: RuntimeToolSseEvent) {
  if (!activity.runId || !event.run_id || activity.runId !== event.run_id) {
    return false
  }
  if (event.invocation_id && activity.invocationId) {
    return event.invocation_id === activity.invocationId
  }
  return Boolean(
    event.turn_id &&
    event.tool_call_id &&
    activity.turnId === event.turn_id &&
    activity.callId === event.tool_call_id,
  )
}

function activitiesMatch(left: ChatToolActivity, right: ChatToolActivity) {
  if (left.id === right.id) {
    return true
  }
  if (!left.runId || left.runId !== right.runId) {
    return false
  }
  if (left.invocationId && right.invocationId) {
    return left.invocationId === right.invocationId
  }
  return Boolean(
    left.turnId && left.callId && left.turnId === right.turnId && left.callId === right.callId,
  )
}

function activityId(event: RuntimeToolSseEvent) {
  if (!event.run_id) {
    return undefined
  }
  const runPrefix = `${event.run_id}:`

  if (event.invocation_id) {
    return `${runPrefix}invocation:${event.invocation_id}`
  }
  if (event.turn_id && event.tool_call_id) {
    return `${runPrefix}turn:${event.turn_id}:call:${event.tool_call_id}`
  }
  return undefined
}

function eventStatus(event: RuntimeToolSseEvent): ChatToolActivityStatus {
  if (event.event === 'tool_end') {
    return event.status
  }
  if (event.event === 'tool_error') {
    // fatal runtime 故障只说明 run 无法继续；dispatch 后的 Invocation truth 仍可能是 unknown。
    return 'unknown'
  }
  return 'running'
}

function shouldApplySequence(current: number | undefined, next: number | undefined) {
  if (next !== undefined) {
    return current === undefined || next >= current
  }

  // 已进入 v2 sequence 后，不能再让无 sequence 的兼容事件覆盖状态。
  return current === undefined
}

function mergeSequenceBounds(
  activity: ChatToolActivity,
  sequence: number | undefined,
): Pick<ChatToolActivity, 'firstSequence' | 'lastSequence'> {
  if (sequence === undefined) {
    return {
      firstSequence: activity.firstSequence,
      lastSequence: activity.lastSequence,
    }
  }
  return {
    firstSequence:
      activity.firstSequence === undefined ? sequence : Math.min(activity.firstSequence, sequence),
    lastSequence:
      activity.lastSequence === undefined ? sequence : Math.max(activity.lastSequence, sequence),
  }
}

function createActivity(event: RuntimeToolSseEvent, id: string): ChatToolActivity {
  const sequence = readSequence(event)
  const status = eventStatus(event)
  const terminal = TERMINAL_TOOL_STATUSES.has(status)

  return {
    callId: event.tool_call_id,
    displayName: event.tool_display_name,
    durationMs: terminal ? event.duration_ms : undefined,
    errorCode: terminal ? event.error_code : undefined,
    fatal: event.event === 'tool_error',
    firstSequence: sequence,
    id,
    invocationId: event.invocation_id,
    lastSequence: sequence,
    progress: event.event === 'tool_progress' ? event.progress : undefined,
    progressSequence: event.event === 'tool_progress' ? sequence : undefined,
    runId: event.run_id ?? undefined,
    status,
    terminalSequence: terminal ? sequence : undefined,
    toolName: event.tool_name,
    total: event.event === 'tool_progress' ? event.total : undefined,
    turnId: event.turn_id,
  }
}

function mergeActivity(activity: ChatToolActivity, event: RuntimeToolSseEvent): ChatToolActivity {
  const sequence = readSequence(event)
  const sequenceBounds = mergeSequenceBounds(activity, sequence)
  const next: ChatToolActivity = {
    ...activity,
    ...sequenceBounds,
    callId: activity.callId ?? event.tool_call_id,
    displayName: activity.displayName ?? event.tool_display_name,
    id: event.invocation_id ? (activityId(event) ?? activity.id) : activity.id,
    invocationId: activity.invocationId ?? event.invocation_id,
    runId: activity.runId ?? event.run_id ?? undefined,
    toolName: activity.toolName ?? event.tool_name,
    turnId: activity.turnId ?? event.turn_id,
  }

  if (event.event === 'tool_progress' && shouldApplySequence(activity.progressSequence, sequence)) {
    next.progress = event.progress
    next.progressSequence = sequence
    next.total = event.total
  }

  if (event.event === 'tool_start' || event.event === 'tool_progress') {
    // start/progress 可以补 correlation 和最早 sequence，但绝不把终态改回 running。
    return next
  }

  if (!shouldApplySequence(activity.terminalSequence, sequence)) {
    return next
  }

  next.durationMs = event.duration_ms
  next.errorCode = event.error_code
  next.fatal = event.event === 'tool_error'
  next.status = eventStatus(event)
  next.terminalSequence = sequence

  return next
}

function compareActivities(left: ChatToolActivity, right: ChatToolActivity) {
  const leftSequence = left.firstSequence ?? Number.MAX_SAFE_INTEGER
  const rightSequence = right.firstSequence ?? Number.MAX_SAFE_INTEGER

  return leftSequence - rightSequence || left.id.localeCompare(right.id)
}

/**
 * 把一条 Tool lifecycle event 合并进消息时间线。
 *
 * correlation 优先使用 run + invocation；只有 invocation 缺失时才接受完整的
 * run + turn + call fallback。call id 单独出现时不能跨 turn 猜测关联。
 */
export function reduceToolActivities(
  activities: ChatToolActivity[],
  event: RuntimeToolSseEvent,
): ChatToolActivity[] {
  const id = activityId(event)

  if (!id) {
    return activities
  }

  const index = activities.findIndex((activity) => eventMatchesActivity(activity, event))
  const nextActivities = [...activities]

  if (index === -1) {
    nextActivities.push(createActivity(event, id))
  } else {
    nextActivities[index] = mergeActivity(nextActivities[index], event)
  }

  return nextActivities.sort(compareActivities)
}

/** 复制 Tool activity，避免 React snapshot 调用方改写 reducer 的 ref state。 */
export function cloneToolActivities(activities: ChatToolActivity[]) {
  return activities.map((activity) => ({ ...activity }))
}

function canJoinToolGroup(
  segment: ChatAssistantSegment,
  activity: ChatToolActivity,
): segment is Extract<ChatAssistantSegment, { type: 'tools' }> {
  return (
    segment.type === 'tools' &&
    Boolean(activity.turnId) &&
    segment.tools.every((tool) => tool.turnId === activity.turnId)
  )
}

/**
 * 将最新 lifecycle truth 同步回它首次出现的位置；后续 answer/thought 不会把 Tool group 顶走。
 * 同一 turn 内连续出现的调用合并为一个可折叠组，没有 turn_id 时保持一调用一组。
 */
export function syncToolActivitySegments(
  segments: ChatAssistantSegment[],
  activities: ChatToolActivity[],
  event: RuntimeToolSseEvent,
) {
  const changedActivity = activities.find((activity) => eventMatchesActivity(activity, event))

  if (!changedActivity) {
    return segments
  }

  const nextSegments: ChatAssistantSegment[] = segments.map((segment) => {
    if (segment.type !== 'tools') {
      return segment
    }

    const tools = segment.tools.map(
      (tool) => activities.find((activity) => activitiesMatch(tool, activity)) ?? tool,
    )

    const firstSequences = tools.flatMap((tool) =>
      tool.firstSequence === undefined ? [] : [tool.firstSequence],
    )
    const lastSequences = tools.flatMap((tool) =>
      tool.lastSequence === undefined ? [] : [tool.lastSequence],
    )

    return {
      ...segment,
      firstSequence: firstSequences.length > 0 ? Math.min(...firstSequences) : undefined,
      lastSequence: lastSequences.length > 0 ? Math.max(...lastSequences) : undefined,
      tools,
    }
  })
  const existingGroup = nextSegments.find(
    (segment) =>
      segment.type === 'tools' &&
      segment.tools.some((tool) => activitiesMatch(tool, changedActivity)),
  )

  if (existingGroup) {
    return nextSegments
  }

  const previousSegment = nextSegments.at(-1)

  if (previousSegment && canJoinToolGroup(previousSegment, changedActivity)) {
    previousSegment.tools.push(changedActivity)
    previousSegment.firstSequence ??= changedActivity.firstSequence
    if (changedActivity.firstSequence !== undefined) {
      previousSegment.firstSequence = Math.min(
        previousSegment.firstSequence ?? changedActivity.firstSequence,
        changedActivity.firstSequence,
      )
    }
    previousSegment.lastSequence ??= changedActivity.lastSequence
    if (changedActivity.lastSequence !== undefined) {
      previousSegment.lastSequence = Math.max(
        previousSegment.lastSequence ?? changedActivity.lastSequence,
        changedActivity.lastSequence,
      )
    }
    return nextSegments
  }

  nextSegments.push({
    firstSequence: changedActivity.firstSequence,
    id: `tool-group-${changedActivity.id}`,
    lastSequence: changedActivity.lastSequence,
    tools: [changedActivity],
    type: 'tools',
  })
  return nextSegments
}

/** 按持久化 reasoning 边界复原 thought → Tool → thought → answer 时间线。 */
export function insertHistoricalToolActivitySegments(
  segments: ChatAssistantSegment[],
  activities: ChatToolActivity[],
  baseId: string,
) {
  if (activities.length === 0 || segments.some((segment) => segment.type === 'tools')) {
    return segments
  }

  const groups: Extract<ChatAssistantSegment, { type: 'tools' }>[] = []

  for (const activity of activities) {
    const previousGroup = groups.at(-1)

    if (previousGroup && canJoinToolGroup(previousGroup, activity)) {
      previousGroup.tools.push(activity)
      previousGroup.lastSequence = Math.max(
        previousGroup.lastSequence ?? 0,
        activity.lastSequence ?? 0,
      )
      continue
    }

    groups.push({
      firstSequence: activity.firstSequence,
      id: `${baseId}-tool-group-${String(groups.length)}`,
      lastSequence: activity.lastSequence,
      tools: [activity],
      type: 'tools',
    })
  }

  const thoughtSegments = segments.filter(
    (segment): segment is Extract<ChatAssistantSegment, { type: 'thought' }> =>
      segment.type === 'thought',
  )
  const answerSegments = segments.filter(
    (segment): segment is Extract<ChatAssistantSegment, { type: 'answer' }> =>
      segment.type === 'answer',
  )
  const legacyThoughts = thoughtSegments.filter((segment) => segment.beforeToolTurnId === undefined)
  const finalThoughts = thoughtSegments.filter((segment) => segment.beforeToolTurnId === null)
  const timeline: ChatAssistantSegment[] = [...legacyThoughts]

  for (const group of groups) {
    const turnId = group.tools[0]?.turnId
    timeline.push(
      ...thoughtSegments.filter(
        (segment) =>
          typeof segment.beforeToolTurnId === 'string' && segment.beforeToolTurnId === turnId,
      ),
      group,
    )
  }

  const matchedTurnIds = new Set(
    groups.flatMap((group) => group.tools.flatMap((tool) => (tool.turnId ? [tool.turnId] : []))),
  )
  const unmatchedAnchoredThoughts = thoughtSegments.filter(
    (segment) =>
      typeof segment.beforeToolTurnId === 'string' && !matchedTurnIds.has(segment.beforeToolTurnId),
  )

  return [...timeline, ...unmatchedAnchoredThoughts, ...finalThoughts, ...answerSegments]
}
